import React, { useCallback, useEffect, useState } from "react";
import { Card, Col, Form, Input, Row, Table, Typography, Button, Space, Divider } from "antd";
import moment from "moment";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { connect } from "react-redux";
import { validationErrors } from "../../utils/Helpers";
import { getCmsActive, getCmsPages, saveCmsNewVersion, sendCmsToRider } from "../../services/cms.service";
import { swalNotification } from "../../components/alret/notificztion";

const { Title, Text } = Typography;

const DEFAULT_PAGE_KEY = "rider_terms";
const DEFAULT_PAGE_TITLE = "Rider Terms & Conditions";

const CmsPage = (props) => {
  const [loadingPages, setLoadingPages] = useState(false);
  const [loadingEditor, setLoadingEditor] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingToRider, setSendingToRider] = useState(false);

  const [pages, setPages] = useState([]);
  const [selectedPageKey, setSelectedPageKey] = useState(DEFAULT_PAGE_KEY);
  const [active, setActive] = useState(null);

  const [form] = Form.useForm();

  const loadPages = useCallback(async () => {
    try {
      setLoadingPages(true);
      const resp = await getCmsPages(props.token, props.emailUser?.id);
      setPages(resp?.data?.data || []);
    } catch (e) {
      validationErrors(e);
    } finally {
      setLoadingPages(false);
    }
  }, [props.token, props.emailUser?.id]);

  const loadSelected = useCallback(async (pageKey) => {
    try {
      setLoadingEditor(true);
      const a = await getCmsActive(props.token, props.emailUser?.id, pageKey);
      const activeRow = a?.data?.data || null;
      setActive(activeRow);
      form.setFieldsValue({
        page_title: activeRow?.page_title || DEFAULT_PAGE_TITLE,
        html_content: activeRow?.html_content || "",
      });
    } catch (e) {
      validationErrors(e);
      setActive(null);
      form.setFieldsValue({ page_title: DEFAULT_PAGE_TITLE, html_content: "" });
    } finally {
      setLoadingEditor(false);
    }
  }, [props.token, props.emailUser?.id, form]);

  useEffect(() => {
    loadPages();
  }, [loadPages]);

  useEffect(() => {
    loadSelected(selectedPageKey);
  }, [loadSelected, selectedPageKey]);

  const saveNewVersion = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await saveCmsNewVersion(props.token, props.emailUser?.id, selectedPageKey, values);
      swalNotification("CMS", "Saved new version successfully", "success");
      await loadPages();
      await loadSelected(selectedPageKey);
    } catch (e) {
      validationErrors(e);
    } finally {
      setSaving(false);
    }
  };

  const sendToRider = async (pageKey) => {
    try {
      setSendingToRider(true);
      await sendCmsToRider(props.token, props.emailUser?.id, pageKey);
      swalNotification("CMS", "Sent to rider successfully", "success");
      await loadPages();
      await loadSelected(selectedPageKey);
    } catch (e) {
      validationErrors(e);
    } finally {
      setSendingToRider(false);
    }
  };

  const pagesColumns = [
    { title: "Page Key", dataIndex: "page_key", key: "page_key" },
    { title: "Page Title", dataIndex: "page_title", key: "page_title", render: (v) => v || "-" },
    { title: "Version", dataIndex: "version", key: "version", render: (v) => v ?? "-" },
    { title: "Status", dataIndex: "is_active", key: "is_active", render: (v) => (v ? "Active" : "Inactive") },
    {
      title: "Updated By",
      dataIndex: "updated_by",
      key: "updated_by",
      render: (v) => (v?.email ? v.email : "-"),
    },
    {
      title: "Last Updated",
      dataIndex: "last_updated",
      key: "last_updated",
      render: (v) => (v ? moment(v).format("DD MMM YYYY – HH:mm") : "-"),
    },
    {
      title: "Request At",
      dataIndex: "sent_to_rider_at",
      key: "sent_to_rider_at",
      render: (v, row) => {
        if (row.page_key !== DEFAULT_PAGE_KEY) return "-";
        return v ? moment(v).format("DD MMM YYYY – HH:mm") : "-";
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, row) => {
        if (row.page_key !== DEFAULT_PAGE_KEY) return "-";
        const alreadySent = !!row.sent_to_rider_at;
        return (
          <Button
            type="primary"
            onClick={(e) => {
              e?.stopPropagation?.();
              sendToRider(row.page_key);
            }}
            loading={sendingToRider}
            disabled={alreadySent || sendingToRider}
          >
            Send to Rider
          </Button>
        );
      },
    },
  ];

  return (
    <div className="page-content">
      <div className="page-content-header">
        <div className="pch">
          <Title level={2}>CMS</Title>
        </div>
      </div>

      <div className="p-content">
        <Card title="CMS Pages" bordered>
          <Table
            rowKey={(r) => r.page_key}
            loading={loadingPages}
            dataSource={pages}
            columns={pagesColumns}
            pagination={false}
            onRow={(record) => ({
              onClick: () => setSelectedPageKey(record.page_key),
            })}
          />
        </Card>

        <Divider />

        <Card title="Rider Terms & Conditions" bordered loading={loadingEditor}>
          <Row gutter={16}>
            <Col span={24}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Text>
                  <strong>Current Version:</strong> {active?.version ?? 0}
                </Text>
                <Text>
                  <strong>Last Updated:</strong>{" "}
                  {active?.updatedAt ? moment(active.updatedAt).format("DD MMM YYYY – HH:mm") : "-"}
                </Text>
              </Space>
            </Col>
          </Row>

          <Divider />

          <Form form={form} layout="vertical">
            <Form.Item
              label="Page Title"
              name="page_title"
              rules={[{ required: true, message: "Page Title is required" }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Content"
              name="html_content"
              rules={[{ required: true, message: "Content is required" }]}
              valuePropName="value"
              trigger="onChange"
              getValueFromEvent={(content) => content}
            >
              <ReactQuill
                theme="snow"
                placeholder="Write terms & conditions..."
                style={{ height: 280, marginBottom: 42 }}
              />
            </Form.Item>

            <Space>
              <Button
                type="primary"
                onClick={saveNewVersion}
                loading={saving}
              >
                Save New Version
              </Button>
            </Space>
          </Form>
        </Card>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  emailUser: state.authReducer.user,
  token: state.authReducer.token,
});

export default connect(mapStateToProps)(CmsPage);

